import { IsoTimestampSchema } from "./string-validation"
import { z } from "zod"

import {
	CheckoutOfferingIdSchema,
	MembershipCycleIdSchema,
	MembershipEnrollmentIdSchema,
	OrderIdSchema,
} from "./common"
import { CheckoutPricingSummarySchema } from "./pricing"

const RECURRING_MEMBERSHIP_FIELD_NAMES = [
	"renewalCadenceDays",
	"paymentLeadDays",
	"reminderLeadDays",
] as const

type RecurringMembershipFieldName = (typeof RECURRING_MEMBERSHIP_FIELD_NAMES)[number]

const recurringMembershipFieldsShape = {
	refillAllowance: z.number().int().nonnegative().nullable(),
	isRecurringMembership: z.boolean(),
	renewalCadenceDays: z.number().int().positive().nullable(),
	firstRenewalDispenseOffsetDays: z.number().int().positive().nullable(),
	paymentLeadDays: z.number().int().nonnegative().nullable(),
	reminderLeadDays: z.number().int().nonnegative().nullable(),
	shippingTransitDays: z.number().int().positive().nullable(),
	deliveryBufferDays: z.number().int().positive().nullable(),
} satisfies z.ZodRawShape

const recurringMembershipFieldsInputShape = {
	refillAllowance: z.number().int().nonnegative().optional(),
	isRecurringMembership: z.boolean().optional(),
	renewalCadenceDays: z.number().int().positive().nullable().optional(),
	firstRenewalDispenseOffsetDays: z.number().int().positive().nullable().optional(),
	paymentLeadDays: z.number().int().nonnegative().nullable().optional(),
	reminderLeadDays: z.number().int().nonnegative().nullable().optional(),
	shippingTransitDays: z.number().int().positive().nullable().optional(),
	deliveryBufferDays: z.number().int().positive().nullable().optional(),
} satisfies z.ZodRawShape

function addRecurringMembershipIssues(
	value: Partial<Record<RecurringMembershipFieldName | "isRecurringMembership" | "refillAllowance", number | boolean | null | undefined>>,
	ctx: z.core.$RefinementCtx<unknown>,
): void {
	if (value.isRecurringMembership === false && typeof value.refillAllowance === "number" && value.refillAllowance > 0) {
		ctx.addIssue({ code: "custom", path: ["refillAllowance"], message: "Later paid refills require recurring billing." })
	}
	if (value.isRecurringMembership !== true) return

	for (const fieldName of RECURRING_MEMBERSHIP_FIELD_NAMES) {
		if (value[fieldName] !== null && value[fieldName] !== undefined) continue
		ctx.addIssue({
			code: "custom",
			path: [fieldName],
			message: "Recurring membership offerings require all schedule fields.",
		})
	}
}

export function withRecurringMembershipValidation<T extends z.ZodObject<z.ZodRawShape>>(schema: T) {
	return schema.superRefine((value, ctx) => {
		addRecurringMembershipIssues(
			value as Partial<Record<RecurringMembershipFieldName | "isRecurringMembership" | "refillAllowance", number | boolean | null | undefined>>,
			ctx,
		)
	})
}

export function withRecurringMembershipInputValidation<T extends z.ZodObject<z.ZodRawShape>>(schema: T) {
	return schema.superRefine((value, ctx) => {
		addRecurringMembershipIssues(
			value as Partial<Record<RecurringMembershipFieldName | "isRecurringMembership" | "refillAllowance", number | boolean | null | undefined>>,
			ctx,
		)
	})
}

export const RecurringMembershipFieldsSchema = z
	.object(recurringMembershipFieldsShape)
	.superRefine((value, ctx) => {
		addRecurringMembershipIssues(value, ctx)
	})
	.meta({ id: "RecurringMembershipFields" })

export type RecurringMembershipFields = z.infer<typeof RecurringMembershipFieldsSchema>

export const RecurringMembershipFieldsInputSchema = z
	.object(recurringMembershipFieldsInputShape)
	.superRefine((value, ctx) => {
		addRecurringMembershipIssues(value, ctx)
	})
	.meta({ id: "RecurringMembershipFieldsInput" })

export type RecurringMembershipFieldsInput = z.infer<typeof RecurringMembershipFieldsInputSchema>

export const MembershipEnrollmentLifecycleStatusSchema = z
	.enum(["PENDING_INITIAL_DISPENSE", "ACTIVE", "PAYMENT_FAILED", "CANCELLED"])
	.meta({ id: "MembershipEnrollmentLifecycleStatus" })

export type MembershipEnrollmentLifecycleStatus = z.infer<
	typeof MembershipEnrollmentLifecycleStatusSchema
>

export const MembershipEnrollmentSetupStatusSchema = z
	.enum(["PENDING_CUSTOMER", "PENDING_WALLET", "READY", "REVIEW_REQUIRED"])
	.meta({ id: "MembershipEnrollmentSetupStatus" })

export type MembershipEnrollmentSetupStatus = z.infer<typeof MembershipEnrollmentSetupStatusSchema>

export const MembershipCycleStatusSchema = z
	.enum([
		"PAID",
		"DISPENSED",
		"SCHEDULED",
		"REMINDER_SENT",
		"PAYMENT_DECLINED",
		"PAYMENT_REVIEW_REQUIRED",
		"CANCELLED",
	])
	.meta({ id: "MembershipCycleStatus" })

export type MembershipCycleStatus = z.infer<typeof MembershipCycleStatusSchema>

export const MembershipEnrollmentSchema = z
	.object({
		id: MembershipEnrollmentIdSchema,
		orderId: OrderIdSchema,
		lifecycleStatus: MembershipEnrollmentLifecycleStatusSchema,
		setupStatus: MembershipEnrollmentSetupStatusSchema,
		refillAllowance: z.number().int().nonnegative().nullable(),
		fulfillmentHoldAt: IsoTimestampSchema.nullable(),
		fulfillmentHoldReason: z.string().nullable(),
		checkoutOfferingId: CheckoutOfferingIdSchema,
		daysSupply: z.number().int().positive(),
		baseRenewalAmountCents: z.number().int().nonnegative().nullable(),
		renewalAmountCents: z.number().int().nonnegative(),
		currency: z.string().min(1),
		renewalCadenceDays: z.number().int().positive(),
		firstRenewalDispenseOffsetDays: z.number().int().positive().nullable(),
		paymentLeadDays: z.number().int().nonnegative(),
		reminderLeadDays: z.number().int().nonnegative(),
		shippingTransitDays: z.number().int().positive().nullable(),
		deliveryBufferDays: z.number().int().positive().nullable(),
		ecryptInitialRequestId: z.string().min(1),
		ecryptInitialTransactionId: z.string().min(1),
		ecryptCustomerId: z.string().nullable(),
		ecryptWalletId: z.string().nullable(),
		initialPaymentNetwork: z.string().nullable(),
		initialMaskedAccountNumber: z.string().nullable(),
		setupAttemptCount: z.number().int().nonnegative(),
		setupNextRetryAt: IsoTimestampSchema.nullable(),
		setupLastError: z.string().nullable(),
		processingLeaseToken: z.string().nullable(),
		processingLeaseExpiresAt: IsoTimestampSchema.nullable(),
		createdAt: IsoTimestampSchema,
		updatedAt: IsoTimestampSchema,
		pricing: CheckoutPricingSummarySchema.optional(),
	})
	.meta({ id: "MembershipEnrollment" })

export type MembershipEnrollment = z.infer<typeof MembershipEnrollmentSchema>

export const MembershipCycleSchema = z
	.object({
		id: MembershipCycleIdSchema,
		membershipEnrollmentId: MembershipEnrollmentIdSchema,
		cycleIndex: z.number().int().nonnegative(),
		cycleStartDate: z.iso.date().nullable(),
		cycleEndDate: z.iso.date().nullable(),
		paymentDueDate: z.iso.date().nullable(),
		reminderDueDate: z.iso.date().nullable(),
		skippedAt: IsoTimestampSchema.nullable(),
		skippedReason: z.string().nullable(),
		status: MembershipCycleStatusSchema,
		reminderDueAt: IsoTimestampSchema.nullable(),
		reminderSentAt: IsoTimestampSchema.nullable(),
		paymentDueAt: IsoTimestampSchema.nullable(),
		paymentAttemptedAt: IsoTimestampSchema.nullable(),
		paymentSucceededAt: IsoTimestampSchema.nullable(),
		paymentFailedAt: IsoTimestampSchema.nullable(),
		dispenseDueAt: IsoTimestampSchema.nullable(),
		actualDispensedAt: IsoTimestampSchema.nullable(),
		paymentAttemptCount: z.number().int().nonnegative(),
		paymentNextRetryAt: IsoTimestampSchema.nullable(),
		paymentLastError: z.string().nullable(),
		paymentAttemptKey: z.string().nullable(),
		ecryptRequestId: z.string().nullable(),
		ecryptTransactionId: z.string().nullable(),
		providerPayload: z.record(z.string(), z.unknown()).default({}),
		processingLeaseToken: z.string().nullable(),
		processingLeaseExpiresAt: IsoTimestampSchema.nullable(),
		createdAt: IsoTimestampSchema,
		updatedAt: IsoTimestampSchema,
	})
	.meta({ id: "MembershipCycle" })

export type MembershipCycle = z.infer<typeof MembershipCycleSchema>

export const MembershipManagementCapabilitiesSchema = z
	.object({
		canRetryEnrollmentSetup: z.boolean(),
		canRetryReminderSend: z.boolean(),
		canRetryRenewalPayment: z.boolean(),
		canCancelEnrollment: z.boolean(),
	})
	.meta({ id: "MembershipManagementCapabilities" })

export type MembershipManagementCapabilities = z.infer<typeof MembershipManagementCapabilitiesSchema>

export const OrderMembershipSchema = z
	.object({
		enrollment: MembershipEnrollmentSchema,
		currentCycle: MembershipCycleSchema.nullable().optional(),
		nextReminderDate: z.iso.date().nullable(),
		nextPaymentDate: z.iso.date().nullable(),
		nextCycleStartDate: z.iso.date().nullable(),
		nextReminderAt: IsoTimestampSchema.nullable().optional(),
		nextPaymentAt: IsoTimestampSchema.nullable().optional(),
		nextDispenseAt: IsoTimestampSchema.nullable().optional(),
		lastError: z.string().nullable().optional(),
		capabilities: MembershipManagementCapabilitiesSchema,
	})
	.meta({ id: "OrderMembership" })

export type OrderMembership = z.infer<typeof OrderMembershipSchema>

export const PortalMembershipEnrollmentSchema = MembershipEnrollmentSchema.omit({
	ecryptInitialRequestId: true,
	ecryptInitialTransactionId: true,
	setupLastError: true,
	processingLeaseToken: true,
	processingLeaseExpiresAt: true,
}).meta({ id: "PortalMembershipEnrollment" })

export type PortalMembershipEnrollment = z.infer<typeof PortalMembershipEnrollmentSchema>

export const PortalMembershipCycleSchema = MembershipCycleSchema.omit({
	paymentAttemptKey: true,
	ecryptRequestId: true,
	ecryptTransactionId: true,
	providerPayload: true,
	processingLeaseToken: true,
	processingLeaseExpiresAt: true,
}).meta({ id: "PortalMembershipCycle" })

export type PortalMembershipCycle = z.infer<typeof PortalMembershipCycleSchema>

export const PortalOrderMembershipSchema = z
	.object({
		enrollment: PortalMembershipEnrollmentSchema,
		currentCycle: PortalMembershipCycleSchema.nullable().optional(),
		nextReminderDate: z.iso.date().nullable(),
		nextPaymentDate: z.iso.date().nullable(),
		nextCycleStartDate: z.iso.date().nullable(),
		nextReminderAt: IsoTimestampSchema.nullable().optional(),
		nextPaymentAt: IsoTimestampSchema.nullable().optional(),
		nextDispenseAt: IsoTimestampSchema.nullable().optional(),
		lastError: z.string().nullable().optional(),
		capabilities: MembershipManagementCapabilitiesSchema,
	})
	.meta({ id: "PortalOrderMembership" })

export type PortalOrderMembership = z.infer<typeof PortalOrderMembershipSchema>
